import { useState } from 'react'
import { TableShell } from '../../components/molecules/TableShell'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Paginator } from '../../components/molecules/Paginator'
import { SupplierFormModal } from './SupplierFormModal'
import { useSuppliers, type SupplierDto } from '../../../viewmodels/useSuppliers'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /manage/suppliers — list + create/edit/delete (name,
 * description, contact). Delete is a soft-delete.
 */
export function SuppliersPage(): JSX.Element {
  const {
    items,
    total,
    page,
    pageSize,
    isLoading,
    error,
    setPage,
    reload,
    create,
    update,
    remove
  } = useSuppliers()
  const { can } = useAuth()
  const [modalMode, setModalMode] = useState<'closed' | 'create' | 'edit'>('closed')
  const [editTarget, setEditTarget] = useState<SupplierDto | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const openCreate = (): void => {
    setEditTarget(null)
    setModalMode('create')
  }
  const openEdit = (row: SupplierDto): void => {
    setEditTarget(row)
    setModalMode('edit')
  }
  const closeModal = (): void => setModalMode('closed')

  const handleDelete = async (row: SupplierDto): Promise<void> => {
    if (typeof window !== 'undefined' && !window.confirm('Are you sure you want to delete?')) return
    setDeletingId(row.id)
    setDeleteError(null)
    const res = await remove(row.id)
    setDeletingId(null)
    if (!res.ok) setDeleteError(res.message)
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="font-heading text-h2 text-charcoal">Suppliers</h1>
        {can(SCREENCODES.MANAGESUPPLIERS, 'add') ? <Button onClick={openCreate}>Add</Button> : null}
      </header>

      {deleteError !== null ? <Alert tone="error">{deleteError}</Alert> : null}
      {error !== null ? (
        <Alert tone="error">
          {error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => void reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <Table
            items={items}
            onEdit={openEdit}
            onDelete={(row) => void handleDelete(row)}
            deletingId={deletingId}
          />
          <Paginator
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            disabled={isLoading}
          />
        </>
      )}

      <SupplierFormModal
        open={modalMode !== 'closed'}
        mode={modalMode === 'edit' ? 'edit' : 'create'}
        initial={editTarget}
        onClose={closeModal}
        onSubmit={async (input) => {
          if (modalMode === 'edit' && editTarget !== null) {
            return update(editTarget.id, input)
          }
          return create(input as Parameters<typeof create>[0])
        }}
      />
    </div>
  )
}

/** Compact pill for the supplier_type column. Two colors so the eye
 *  can scan Raw material vs Artisan at a glance. */
function SupplierTypePill({ type }: { readonly type: SupplierDto['supplier_type'] }): JSX.Element {
  const isArtisan = type === 'artisan'
  return (
    <span
      className={[
        'inline-flex items-center rounded-full px-2.5 py-0.5 font-body text-body-xs',
        isArtisan
          ? 'border border-forest/30 bg-forest/10 text-forest'
          : 'border border-border bg-parchment/60 text-text-secondary'
      ].join(' ')}
    >
      {isArtisan ? 'Artisan' : 'Raw material'}
    </span>
  )
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No suppliers yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Use <span className="font-medium">Add</span> at the top right to create
        the first supplier.
      </p>
    </div>
  )
}

function Table({
  items,
  onEdit,
  onDelete,
  deletingId
}: {
  readonly items: SupplierDto[]
  readonly onEdit: (row: SupplierDto) => void
  readonly onDelete: (row: SupplierDto) => void
  readonly deletingId: string | null
}): JSX.Element {
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.MANAGESUPPLIERS, 'edit')
  const canDelete = can(SCREENCODES.MANAGESUPPLIERS, 'delete')
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Name</Th>
            <Th>Type</Th>
            <Th>Description</Th>
            <Th>Contact</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.id} className="border-t border-border">
              <Td>{row.name}</Td>
              <Td><SupplierTypePill type={row.supplier_type} /></Td>
              <Td
                className="max-w-[24rem] truncate"
                title={row.description === null ? '' : row.description}
              >
                {row.description ?? <span className="text-text-secondary">—</span>}
              </Td>
              <Td
                className="max-w-[20rem] truncate"
                title={row.contact_info === null ? '' : row.contact_info}
              >
                {row.contact_info ?? <span className="text-text-secondary">—</span>}
              </Td>
              <Td className="text-right whitespace-nowrap">
                {canEdit ? (
                  <>
                    <Button size="sm" variant="secondary" onClick={() => onEdit(row)}>
                      Edit
                    </Button>{' '}
                  </>
                ) : null}
                {canDelete ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(row)}
                    disabled={deletingId === row.id}
                  >
                    {deletingId === row.id ? 'Deleting…' : 'Delete'}
                  </Button>
                ) : null}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  )
}
